import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type {
  ProductListResponse,
  ProductOptionGroupInput,
  ProductVariantInput,
  ProductView,
} from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateSellerCatalog } from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";

/**
 * Patch the product-detail + products-list caches with a freshly-returned
 * product, then invalidate the catalog. Shared by every granular variant
 * mutation so they all keep the list and editor in sync without a manual
 * refetch (mirrors useUpdateProductMutation).
 */
async function syncProductCaches(
  queryClient: QueryClient,
  storeSlug: string,
  productId: string,
  data: ProductView,
) {
  queryClient.setQueryData(
    queryKeys.seller.productById(storeSlug, productId),
    data,
  );
  queryClient.setQueriesData<ProductListResponse>(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "seller" &&
        query.queryKey[1] === storeSlug &&
        query.queryKey[2] === "products" &&
        query.queryKey[3] === "list",
    },
    (current) =>
      current
        ? {
            ...current,
            items: current.items.map((item) =>
              item.id === data.id ? data : item,
            ),
          }
        : current,
  );
  await invalidateSellerCatalog(queryClient, storeSlug);
  await queryClient.invalidateQueries({
    queryKey: queryKeys.seller.productById(storeSlug, productId),
    exact: true,
  });
}

// ── Update one variant's mutable fields (stock / price / discount / etc.) ─────

export type UpdateVariantFields = {
  stockCount?: number;
  price?: number;
  discountedPrice?: number | null;
  cost?: number | null;
  forceOutOfStock?: boolean;
  isActive?: boolean;
};

export function useUpdateVariantMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      productId,
      variantId,
      fields,
    }: {
      storeSlug: string;
      productId: string;
      variantId: string;
      fields: UpdateVariantFields;
    }) =>
      apiJsonClient<ProductView>(
        endpoints.seller.productVariant(productId, variantId),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
        },
      ),
    onSuccess: (data, variables) =>
      syncProductCaches(
        queryClient,
        variables.storeSlug,
        variables.productId,
        data,
      ),
  });
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ── Update one option value's labels / colour ─────────────────────────────────

export type UpdateOptionValueFields = {
  valueAr?: string;
  valueEn?: string | null;
  colorHex?: string | null;
};

export function useUpdateOptionValueMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      productId,
      valueId,
      fields,
    }: {
      storeSlug: string;
      productId: string;
      valueId: string;
      fields: UpdateOptionValueFields;
    }) =>
      apiJsonClient<ProductView>(
        endpoints.seller.productOptionValue(productId, valueId),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
        },
      ),
    onSuccess: (data, variables) =>
      syncProductCaches(
        queryClient,
        variables.storeSlug,
        variables.productId,
        data,
      ),
  });
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ── Delete one variant ────────────────────────────────────────────────────────

export function useDeleteVariantMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      productId,
      variantId,
    }: {
      storeSlug: string;
      productId: string;
      variantId: string;
    }) =>
      apiJsonClient<ProductView>(
        endpoints.seller.productVariant(productId, variantId),
        { method: "DELETE" },
      ),
    onSuccess: (data, variables) =>
      syncProductCaches(
        queryClient,
        variables.storeSlug,
        variables.productId,
        data,
      ),
  });
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ── Soft-delete many variants in one transaction ──────────────────────────────

export function useBulkDeleteVariantsMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      productId,
      variantIds,
    }: {
      storeSlug: string;
      productId: string;
      variantIds: string[];
    }) =>
      apiJsonClient<ProductView>(
        endpoints.seller.productVariantsBulkDelete(productId),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ variantIds }),
        },
      ),
    onSuccess: (data, variables) =>
      syncProductCaches(
        queryClient,
        variables.storeSlug,
        variables.productId,
        data,
      ),
  });
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ── Append new variants (+ any new option groups / values) ────────────────────

export function useAddVariantsMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      productId,
      optionGroups,
      variants,
    }: {
      storeSlug: string;
      productId: string;
      optionGroups: ProductOptionGroupInput[];
      variants: ProductVariantInput[];
    }) =>
      apiJsonClient<ProductView>(endpoints.seller.productVariants(productId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ optionGroups, variants }),
      }),
    onSuccess: (data, variables) =>
      syncProductCaches(
        queryClient,
        variables.storeSlug,
        variables.productId,
        data,
      ),
  });
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
